import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import PortSearch from '../components/PortSearch';
import { Button, Field, Tag, Badge, Card } from '../components/sv';
import { useAuth } from '../context/AuthContext';
import { Loader2, ArrowRight } from 'lucide-react';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import { toast } from 'sonner';

const SERVICE_TONE = { Express: 'success', Standard: 'info', Economy: 'neutral' };

export default function SchedulesPage() {
  const [searchParams] = useSearchParams();
  const { isAgent: staff } = useAuth();
  const [ports, setPorts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [origin, setOrigin] = useState(searchParams.get('origin') || '');
  const [destination, setDestination] = useState(searchParams.get('destination') || '');
  const [dateFrom, setDateFrom] = useState('');

  useEffect(function () {
    api.get('/ports').then(function (r) { setPorts(r.data); }).catch(function () {});
    if (searchParams.get('origin') && searchParams.get('destination')) runSearch(searchParams.get('origin'), searchParams.get('destination'), '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runSearch(o, d, df) {
    setLoading(true); setSearched(true); setSearchError(null);
    try {
      const params = new URLSearchParams({ origin: o, destination: d });
      if (df) params.append('departure_from', df);
      const res = await api.get('/schedules/search?' + params.toString());
      setSchedules(res.data.schedules);
      setRouteInfo({ origin: res.data.origin, destination: res.data.destination, distance: res.data.distance_nm, total: res.data.total_results });
    } catch (err) {
      const message = err.response?.data?.detail || 'Search failed. Try again.';
      toast.error(message); setSearchError(message); setSchedules([]); setRouteInfo(null);
    } finally { setLoading(false); }
  }

  function handleSearch(e) {
    e.preventDefault();
    if (!origin || !destination) { toast.error('Select both origin and destination ports'); return; }
    runSearch(origin, destination, dateFrom);
  }

  function daysUntil(d) { const n = differenceInCalendarDays(parseISO(d), new Date()); return n < 0 ? 'Sailed ' + (-n) + (n === -1 ? ' day' : ' days') + ' ago' : n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : 'In ' + n + ' days'; }

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="schedules-page">
      <div className="bg-white border-b border-gray-200">
        <div className="sv-wrap py-10 md:py-12">
          <p className="sv-kicker">Schedules</p>
          <h1 className="text-[36px] md:text-[40px] sv-display mt-2">Point-to-point sailings</h1>
          <form onSubmit={handleSearch} className="grid md:grid-cols-[1fr_1fr_200px_auto] gap-4 items-end mt-8">
            <Field label="From"><PortSearch ports={ports} value={origin} onChange={setOrigin} placeholder="Port, country or code" testId="schedule-origin" /></Field>
            <Field label="To"><PortSearch ports={ports} value={destination} onChange={setDestination} placeholder="Port, country or code" testId="schedule-destination" /></Field>
            <Field label="Departing from" hint={staff ? 'Past dates show sailings that already departed.' : undefined}><input type="date" value={dateFrom} min={staff ? undefined : new Date().toISOString().slice(0, 10)} onChange={function (e) { setDateFrom(e.target.value); }} className="sv-input sv-mono" data-testid="schedule-departure-date" /></Field>
            <Button type="submit" disabled={loading} data-testid="schedule-search-btn">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Search'}</Button>
          </form>
        </div>
      </div>

      <div className="sv-wrap py-8 pb-24">
        {routeInfo && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-testid="schedule-route-info">
            <div className="flex items-center gap-3 text-xl"><span>{routeInfo.origin}</span><ArrowRight className="h-5 w-5 text-gray-600" strokeWidth={1.75} /><span>{routeInfo.destination}</span></div>
            <div className="text-sm text-gray-600"><span className="sv-mono">{routeInfo.distance.toLocaleString()} nm</span> · {routeInfo.total} sailings</div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gray-600" /></div>
        ) : schedules.length > 0 ? (
          <div className="sv-card overflow-x-auto">
            <table className="sv-table" data-testid="schedules-table">
              <thead><tr><th>Vessel / voyage</th><th>Service</th><th>Departure</th><th>Transit</th><th>Arrival</th><th>Cut-off</th><th>Space</th><th></th></tr></thead>
              <tbody>
                {schedules.map(function (s) {
                  return (
                    <tr key={s.id} data-testid={"schedule-row-" + s.id}>
                      <td><div>{s.vessel_name}</div><div className="sv-mono text-[13px] text-gray-600">{s.voyage_number}{s.service_route ? ' · ' + s.service_route : ''}</div></td>
                      <td><Badge tone={SERVICE_TONE[s.service_type] || 'neutral'}>{s.service_type}</Badge></td>
                      <td><div className="sv-mono text-sm">{format(parseISO(s.departure_date), 'dd MMM yyyy')}</div><div className={"text-[13px] " + (s.departed ? 'text-amber-800' : 'text-gray-600')} data-testid={"schedule-days-" + s.id}>{daysUntil(s.departure_date)}</div></td>
                      <td className="sv-mono text-sm">{s.transit_days} days</td>
                      <td className="sv-mono text-sm">{format(parseISO(s.arrival_date), 'dd MMM yyyy')}</td>
                      <td className="sv-mono text-sm">{format(parseISO(s.cutoff_date), 'dd MMM, HH:mm')}</td>
                      <td><Tag mono>{s.available_space} TEU</Tag></td>
                      <td className="text-right">{s.departed && !staff ? <Tag>Sailed</Tag> : <Button size="sm" to="/booking" state={{ schedule: s }} data-testid={"schedule-book-" + s.id}>{s.departed ? 'Book retrospectively' : 'Book'}</Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : searched && searchError ? (
          <Card data-testid="schedule-search-error"><p className="text-lg">Search unavailable</p><p className="text-gray-700 mt-2">{searchError}</p><Button variant="secondary" className="mt-6" onClick={handleSearch} data-testid="schedule-search-retry">Try again</Button></Card>
        ) : searched ? (
          <Card data-testid="schedule-empty"><p className="text-lg">No sailings found</p><p className="text-gray-700 mt-2">Try a different date or a nearby port. Our agents can also arrange transhipment options — <Link to="/contact" className="sv-link">contact us</Link>.</p></Card>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {[['Fixed-day weekly departures', 'Every loop sails on a published weekday; cut-offs are 48 hours before departure.'], ['Direct and transhipment', 'Search returns direct sailings first, then the fastest single-transhipment options.'], ['Book straight from a sailing', 'Choose a sailing and the booking form is pre-filled with vessel, voyage and dates.']].map(function (c) { return <Card key={c[0]}><p className="text-lg">{c[0]}</p><p className="text-gray-700 mt-2 text-[15px]">{c[1]}</p></Card>; })}
          </div>
        )}
      </div>
    </div>
  );
}
